import type { PdfjsLike } from '../parsers';
import type { ErrorStep } from '../diagnostics';
import { needsLegacyPdfjs } from '../parsers/pdfjsBuild';
import { startPdfWorker, type PdfWorkerMode, type WorkerCtor } from '../upload/pdfReader';
import pdfClassicWorkerUrl from '../parsers/pdf.classic.worker.ts?worker&url';

/**
 * PDF.js in the browser, with a ladder of configurations (iPhone, 2026-10-11: every PDF failed on real
 * iOS while Playwright's WebKit passed). The build this browser should run (src/parsers/pdfjsBuild.ts:
 * legacy on any Apple WebKit), each starting its worker with fallbacks (src/upload/pdfReader.ts). When a
 * read fails in the reader (not a damaged file or a password) or stalls, the next configuration is
 * tried: modern → legacy → legacy on the main thread. What works is remembered for the session.
 */
export interface ReaderConfig {
  legacy: boolean;
  /** The first worker mode to try (later ones are fallbacks). */
  from: PdfWorkerMode;
}
export interface LoadedReader {
  pdfjs: PdfjsLike;
  legacy: boolean;
  mode: PdfWorkerMode;
}

export function readerLadder(legacyOnly: boolean): ReaderConfig[] {
  return [...(legacyOnly ? [] : [{ legacy: false, from: 'module' as const }]), { legacy: true, from: 'module' }, { legacy: true, from: 'main' }];
}

const ladder = readerLadder(needsLegacyPdfjs());
let step = 0;
const loaded = new Map<string, Promise<LoadedReader>>();

/** The configuration currently believed to work (for the debug panel too). */
export const currentReaderConfig = (): ReaderConfig | undefined => ladder[step];

/** A read must finish within this (a worker killed for memory, or stalled, never answers). Tests may lower it. */
export const readerTimeoutMs = (): number => (globalThis as { __magiaReaderTimeoutMs?: number }).__magiaReaderTimeoutMs ?? 45_000;

async function load(cfg: ReaderConfig, onError: (stage: ErrorStep, err: unknown) => void): Promise<LoadedReader> {
  let modules;
  try {
    modules = cfg.legacy
      ? await Promise.all([import('pdfjs-dist/legacy/build/pdf.mjs'), import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')])
      : await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
  } catch (err) {
    onError('pdfjsLoad', err);
    throw err;
  }
  const [pdfjs, worker] = modules;
  const mode = await startPdfWorker(pdfjs as never, {
    Worker: cfg.from === 'main' || typeof Worker === 'undefined' ? undefined : (Worker as unknown as WorkerCtor),
    moduleUrl: worker.default,
    classicUrl: pdfClassicWorkerUrl,
    importOnMainThread: () => import(/* @vite-ignore */ worker.default),
    onError,
  });
  return { pdfjs: pdfjs as unknown as PdfjsLike, legacy: cfg.legacy, mode };
}

/** PDF.js in the configuration currently believed to work; a configuration that can't load moves the ladder on. */
export async function loadPdfjs(onError: (stage: ErrorStep, err: unknown) => void): Promise<LoadedReader> {
  for (; step < ladder.length; step++) {
    const cfg = ladder[step] as ReaderConfig;
    const key = `${cfg.legacy}-${cfg.from}`;
    if (!loaded.has(key)) loaded.set(key, load(cfg, onError));
    try {
      return await (loaded.get(key) as Promise<LoadedReader>);
    } catch {
      loaded.delete(key);
    }
  }
  step = ladder.length - 1;
  throw Object.assign(new Error('no PDF reader configuration could start'), { name: 'Error' });
}

/**
 * Runs `read` with the reader, moving down the ladder while it fails in the reader (`readerFailed`) or
 * times out. Returns the last result; `undefined` when no configuration could even load.
 */
export async function readWithLadder<R extends { problem?: string }>(
  read: (pdfjs: PdfjsLike) => Promise<R>,
  onError: (stage: ErrorStep, err: unknown) => void,
): Promise<{ result: R; reader: LoadedReader } | undefined> {
  let last: { result: R; reader: LoadedReader } | undefined;
  for (;;) {
    let reader: LoadedReader;
    try {
      reader = await loadPdfjs(onError);
    } catch {
      return last;
    }
    const timedOut = Symbol('timeout');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const outcome = await Promise.race([
      read(reader.pdfjs),
      new Promise<typeof timedOut>((resolve) => (timer = setTimeout(() => resolve(timedOut), readerTimeoutMs()))),
    ]).finally(() => clearTimeout(timer));
    if (outcome === timedOut) onError('readerTimeout', Object.assign(new Error('the PDF reader did not answer in time'), { name: 'Timeout' }));
    else {
      last = { result: outcome, reader };
      if (outcome.problem !== 'readerFailed') return last;
    }
    // This configuration can't read here: drop it (stopping a stalled worker) and try the next one.
    const cfg = ladder[step];
    if (!cfg || step >= ladder.length - 1) return last;
    const port = (reader.pdfjs as unknown as { GlobalWorkerOptions?: { workerPort?: { terminate?: () => void } | null } }).GlobalWorkerOptions?.workerPort;
    if (outcome === timedOut) port?.terminate?.();
    loaded.delete(`${cfg.legacy}-${cfg.from}`);
    step++;
  }
}
